import { ApiError } from "@/lib/api/client";

// Friendly Vietnamese messages keyed by API error code. These are the fallback
// when BE doesn't supply a usable `details` string.
const CODE_MAP: Record<string, string> = {
  unauthorized: "Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.",
  forbidden: "Bạn không có quyền thực hiện thao tác này.",
  not_found: "Không tìm thấy dữ liệu yêu cầu.",
  validation_error: "Dữ liệu nhập chưa hợp lệ, vui lòng kiểm tra lại.",
  unprocessable: "Yêu cầu chưa hợp lệ, vui lòng kiểm tra lại.",
  bad_request: "Yêu cầu chưa hợp lệ, vui lòng kiểm tra lại.",
  conflict: "Dữ liệu đã tồn tại hoặc bị xung đột, vui lòng tải lại trang.",
  rate_limited: "Bạn thao tác quá nhanh, vui lòng thử lại sau ít phút.",
  internal_error: "Hệ thống đang gặp sự cố, vui lòng thử lại sau.",
  network_error: "Không thể kết nối tới máy chủ, kiểm tra kết nối mạng.",
};

// Common English detail strings emitted by BE → user-friendly Vietnamese.
// Only add entries that are likely to be shown to end users.
const DETAIL_MAP: Record<string, string> = {
  "invalid credentials": "Sai tên đăng nhập hoặc mật khẩu.",
  "account disabled": "Tài khoản đã bị vô hiệu hoá, liên hệ quản trị viên.",
  "current password incorrect": "Mật khẩu hiện tại không đúng.",
  "email already in use": "Email đã được sử dụng.",
  "phone already registered": "Số điện thoại đã được đăng ký.",
  "missing bearer token": "Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.",
  "invalid or expired token": "Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.",
  "invalid or expired ticket": "Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.",
  "invalid refresh token": "Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.",
  "refresh token reuse detected": "Phiên đăng nhập không hợp lệ, vui lòng đăng nhập lại.",
  "insufficient role": "Bạn không có quyền thực hiện thao tác này.",
  "insufficient stock": "Không đủ tồn kho để thực hiện thao tác.",
  "order not found": "Không tìm thấy đơn hàng.",
  "order is not a draft": "Đơn hàng không ở trạng thái nháp, không thể chỉnh sửa.",
  "order has no items": "Đơn hàng chưa có sản phẩm nào.",
  "order total must be positive": "Tổng tiền đơn phải lớn hơn 0.",
  "only paid orders can be voided": "Chỉ có thể huỷ đơn đã thanh toán.",
  "only admin/manager can void orders": "Chỉ quản trị viên hoặc quản lý có thể huỷ đơn.",
  "partial payment requires a customer": "Thanh toán một phần cần chọn khách hàng.",
  "item not found": "Không tìm thấy sản phẩm trong đơn.",
  "variant not found or inactive": "Sản phẩm không tồn tại hoặc đã ngưng kinh doanh.",
  "product not found": "Không tìm thấy sản phẩm.",
  "category not found": "Không tìm thấy danh mục.",
  "customer not found": "Không tìm thấy khách hàng.",
  "customer_id required": "Vui lòng chọn khách hàng.",
  "coupon not found": "Không tìm thấy mã giảm giá.",
  "coupon inactive": "Mã giảm giá đã ngưng hoạt động.",
  "coupon is no longer valid": "Mã giảm giá không còn hiệu lực.",
  "code required": "Vui lòng nhập mã.",
  "amount must be positive": "Số tiền phải lớn hơn 0.",
  "from must be before to": "Ngày bắt đầu phải trước ngày kết thúc.",
  "from/to must be YYYY-MM-DD": "Định dạng ngày phải là YYYY-MM-DD.",
  "no fields to update": "Không có dữ liệu nào thay đổi để cập nhật.",
  "cannot delete the last active admin": "Không thể xoá quản trị viên cuối cùng.",
  "import not found": "Không tìm thấy phiếu nhập.",
  "import is not in draft status": "Phiếu nhập không ở trạng thái nháp.",
  "import already committed or cancelled": "Phiếu nhập đã hoàn tất hoặc đã huỷ.",
  "import has no items": "Phiếu nhập chưa có sản phẩm.",
  "serial not found": "Không tìm thấy serial.",
  "SePay bank config not set": "Chưa cấu hình tài khoản ngân hàng SePay.",
  "CSV export is limited to once per minute": "Chỉ có thể xuất CSV mỗi phút một lần.",
  "q is required": "Vui lòng nhập từ khoá tìm kiếm.",
  "topic required": "Thiếu tham số topic.",
  "invalid topic": "Topic không hợp lệ.",
  "forbidden_topic": "Bạn không có quyền truy cập topic này.",
};

// Vietnamese has accented characters not present in plain ASCII English.
// Used to detect whether BE already returned a Vietnamese detail we can show as-is.
const VIETNAMESE_CHAR = /[àáảãạăằắẳẵặâầấẩẫậèéẻẽẹêềếểễệìíỉĩịòóỏõọôồốổỗộơờớởỡợùúủũụưừứửữựỳýỷỹỵđÀÁẢÃẠĂẰẮẲẴẶÂẦẤẨẪẬÈÉẺẼẸÊỀẾỂỄỆÌÍỈĨỊÒÓỎÕỌÔỒỐỔỖỘƠỜỚỞỠỢÙÚỦŨỤƯỪỨỬỮỰỲÝỶỸỴĐ]/;

function fromDetails(details: unknown): string | null {
  if (typeof details !== "string") return null;
  const trimmed = details.trim();
  if (!trimmed) return null;
  const mapped = DETAIL_MAP[trimmed.toLowerCase()];
  if (mapped) return mapped;
  // BE often emits Vietnamese details directly (e.g. "Lý do ghi nợ tối thiểu 10 ký tự") — show as-is.
  if (VIETNAMESE_CHAR.test(trimmed)) return trimmed;
  return null;
}

/**
 * Translate any thrown error to a user-facing Vietnamese sentence.
 * Resolution order: BE detail → BE code → Error.message → fallback.
 */
export function translateApiError(err: unknown): string {
  if (err instanceof ApiError) {
    const fromDetail = fromDetails(err.details);
    if (fromDetail) return fromDetail;
    if (err.code && CODE_MAP[err.code]) return CODE_MAP[err.code];
    // Unknown code — only show raw message if it looks Vietnamese, else generic fallback.
    if (err.message && VIETNAMESE_CHAR.test(err.message)) return err.message;
    return "Đã xảy ra lỗi, vui lòng thử lại.";
  }
  if (err instanceof Error && VIETNAMESE_CHAR.test(err.message)) {
    return err.message;
  }
  return "Đã xảy ra lỗi, vui lòng thử lại.";
}
